import React, { useRef, useState } from 'react'
import './template-games.styles.scss'
import BackButton from '@/components/SHARED/back-button/back-button.component'
import { useNavigate } from 'react-router-dom'
import ImageContainer from '@/components/SHARED/image-container/image-container.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLayerGroup, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import Modal, { ModalAlert, ModalConfirm } from '@/components/modal/modal.component'
import { format } from 'date-fns'
import { UseCloudFunction } from '@/utils/firebase.utils'
import { useSelector } from 'react-redux'
import FullPageLoader from '@/components/loader/full-page-loader.component'

const AdminTemplateGames = ({
  owners,
  packageList,
  templateGames
}) => {

  const userProfile = useSelector((state) => state.userData.userProfile);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [ formData, setFormData ] = useState({});
  const [ confirmMessage, setConfirmMessage ] = useState(null);
  const [ alertMessage, setAlertMessage ] = useState(null);
  const [ loading, setLoading ] = useState(false);
  const navigate = useNavigate();
  const dataRef = useRef({});
  const approveRef = useRef();

  function saveData(key, value) {
    if (key === 'openDate' || key === 'closeDate') {
      const [year, month, day] = value.split('-').map(Number);
      value = new Date(year, month - 1, day).getTime();
    }
    dataRef.current = {
      ...dataRef.current,
      [key]: value
    }
    setFormData(dataRef.current);
    console.log(dataRef.current);
  }

  function saveTemplateToContent(gameId) {
    const tempData = {...dataRef.current};
    if (tempData.accessLevel === undefined) {
      setAlertMessage('Please select an access level for this template.');
      return;
    }
    
    setConfirmMessage('Are you sure you want to set this game as a template? ' +
      'This will create a new content package based on this game, copying all elements and teams. ' + 
      'This action cannot be undone.');
    approveRef.current = () => {
      continueSave();
    }

    async function continueSave() {
    setLoading(true);
    const game = templateGames[gameId];
    const dataToSave = {
      "access": Number(tempData.accessLevel) || 0,
      "description": game.desc,
      "icon": game.icon,
      "name": game.gameName,
      "closeDate": tempData.closeDate || null,
      "openDate": tempData.openDate || null,
      "status": "active",
      "type": "templateGame",
      "gameData": {
        "desc": game.desc,
        "elementCount": game.elementCount,
        "gameId": game.gameId,
        "id": game.id,
        "gameName": game.gameName,
        "path": game.path,
        "icon": game.icon,
      },
    }
    const res = await UseCloudFunction(
      'saveTemplateToContentLibrary',
      {
        'templateData': dataToSave,
        'appId': userProfile.appId,
      }
    )
    console.log(res);
    setLoading(false);
    if (res.error) {
      setAlertMessage('Error saving template: ' + res.error);
      return;
    }
    setAlertMessage('Template saved successfully!');    
    setShowTemplateModal(null);
  }
}

  return (
    <div className='admin-template-games'>
      <div className='admin-template-games-content'>
        <BackButton cancel={() => navigate(-1)} />
        <div className="g-space-1"></div>
        <div className='section-title with-icon'>
          <span>Template Games</span>
        </div>
        <hr />
        <div className='admin-template-games-content__sub'>
          {
            (templateGames && Object.keys(templateGames).length > 0)
              ?
              <div className='admin-template-games-list'>
                <table className='g-table'>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Name</th>
                      <th className='centered'>Elements</th>
                      <th className='centered'>Teams</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      Object.keys(templateGames).map((gameId, index) => {
                        const game = templateGames[gameId];
                        return (
                          <tr key={index}>
                            <td className='game-icon'>
                              <ImageContainer src={game.icon} alt={game.gameName} />
                            </td>
                            <td><h3>{game.gameName}</h3></td>
                            <td className='centered'>{game.createdElementTotal ?? 0}</td>
                            <td className='centered'>{game.createdTeams ?? 0}</td>
                            <td>
                              <div className='buttons'>
                                <button
                                  className='g-button small-btn'
                                  title="View Game Details"
                                  onClick={() => navigate(`/admin/games/${gameId}`)}>
                                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                                </button>
                                <button
                                  className='g-button small-btn'
                                  title="Set Game as Template"
                                  onClick={() => setShowTemplateModal(gameId)}>
                                  <FontAwesomeIcon icon={faLayerGroup} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    }
                  </tbody>
                </table>
              </div>
              :
              <div className='admin-template-games-list__empty'>
                <span>No template games available</span>
              </div>
          }
        </div>
      </div>
      <Modal
        show={showTemplateModal}
        cancel={() => setShowTemplateModal(null)}
        text="Set as Template"
        closeButton={true}
        cls="narrow">
        <div className='admin-template-games__template-modal'>
          <h3>Set "{showTemplateModal && templateGames[showTemplateModal].gameName}" as a template?</h3>
          <p>This will allow users to use this game as a starting point for new games, 
            copying all elements and teams.
          </p>
          <div className="g-space-1"></div>
          <div className='admin-template-games__template-modal__game-info'>
            <div className='field small'>
              <label>Access Level</label>
              <select 
                className='med-input'
                value={formData.accessLevel.toString() || ''}
                onChange={(e) => saveData('accessLevel', Number(e.target.value))}>
                <option value={undefined}>Select Access Level</option>
                <option value="0">Free</option>
                <option value="1">Pro Level</option>
                <option value="2">Premium Level</option>
              </select>
            </div>
            <div className="g-space-1"></div>
            <div className='field small'>
              <label>Open Date (Optional)</label>
              <input
                className='med-input'
                type="date"
                value={
                  formData.openDate 
                  ? format(new Date(formData.openDate), "yyyy-MM-dd") 
                  : ''
                }
                onChange={(e) => saveData('openDate', e.target.value)}
              />
            </div>
            <div className="g-space-1"></div>
            <div className='field small'>
              <label>Close Date (Optional)</label>
              <input
                className='med-input'
                type="date"
                value={
                  formData.closeDate 
                  ? format(new Date(formData.closeDate), "yyyy-MM-dd") 
                  : ''
                }
                onChange={(e) => saveData('closeDate', e.target.value)}
              />
            </div>
          </div>
          <div className="g-space-1"></div>
          <div className='buttons'>
            <button
              className='g-button primary'
              onClick={() => saveTemplateToContent(showTemplateModal)}>
              Save
            </button>
          </div>
        </div>
      </Modal>
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default AdminTemplateGames